import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Directive, inject, OnDestroy, OnInit, ViewContainerRef } from "@angular/core";
import * as _ from "lodash";
import { forkJoin, Observable, Subject } from "rxjs";
import { first, map, share, tap } from "rxjs/operators";

import { LgConsole } from "@logex/framework/core";
import { LgStorageLoader } from "@logex/framework/lg-application";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { LgConfirmationDialog } from "@logex/framework/ui-core";

import { LoaderArgumentsMap, LoadManager } from "@shared/services/load-manager";

import { AppVersionSettings, Scenario } from "@shared";
import { AppScenario } from "@shared/scenario/app-scenario";
import { ScenarioService } from "@shared/scenario/scenario-service";
import { ScenarioSettingsService } from "@shared/scenario/scenario-settings-service";
import { AppDefinitions, DefinitionKey } from "../app-definitions.service";
import { AuthorizationBase } from "./authorization-base";

/**
 * Describes an UI block of the page. It is named, so you can reference it by name and
 * use, for example, in page's markup to know if the block is visible.
 * Every block defines criteria of visibility and declares which names data sets
 * it requires.
 */
export interface PageBlock {
    /**
     * Function that determines if the block is visible now or not.
     *
     * @returns {}
     */
    visible: () => boolean;

    /**
     * Array of names of data sets that are used by this UI block.
     */
    dataSets: string[];
}

/**
 * Extended [[IPageBlock]] that knows how to check if it is still loading.
 */
export interface PageBlockExt extends PageBlock {
    isLoading: () => boolean;
    isLoaded: () => boolean;
}

// ----------------------------------------------------------------------------------
//
@Directive()
export abstract class PageComponentBase<
        TPermissions extends AuthorizationBase,
        TVersion extends Scenario
    >
    implements OnInit, OnDestroy
{
    // ----------------------------------------------------------------------------------
    // Fields
    _initialized = false;
    protected _destroyed$ = new Subject<void>();
    protected _forceLoadingOverlay = false;
    protected _preparationFailed = false;
    protected _version: TVersion;
    protected _scenarioSettings: AppVersionSettings;
    protected _pageBlocks: _.Dictionary<PageBlockExt>;

    protected _lgConsole = inject(LgConsole);
    protected _confirm = inject(LgConfirmationDialog);
    protected _viewContainerRef = inject(ViewContainerRef);
    protected _versionService = inject(ScenarioService<AppScenario>);
    protected _scenarioSettingsService = inject(ScenarioSettingsService);

    protected _storageLoader = inject(LgStorageLoader);
    protected _lgTranslate = inject(LgTranslateService);
    protected _loadManager = inject(LoadManager);
    protected _cdr = inject(ChangeDetectorRef);

    readonly: boolean;

    public definitions = inject(AppDefinitions);

    public abstract authorization: TPermissions;

    // ----------------------------------------------------------------------------------
    //
    constructor() {
        this._confirm = this._confirm.bindViewContainerRef(this._viewContainerRef);
    }

    public get hospitalAbbreviation(): string {
        return this._scenarioSettings?.hospital_abbreviation;
    }

    // ----------------------------------------------------------------------------------
    // Page activation
    public ngOnInit(): void {
        this._prepare().subscribe({
            complete: () => {
                this._activate();
            },
            error: err => {
                this._preparationFailed = true;
                this._lgConsole.error("Error initializing page", err);
            }
        });
    }

    protected _prepare(): Observable<any[]> {
        this._configureLoadManager();
        this._configurePageBlocks();

        return forkJoin([
            this._versionService.version.pipe(first()),
            this._scenarioSettingsService.scenarioSettings.pipe(first()),
            this.definitions.load(...this._getRequiredDefinitions()),
            this._storageLoader.loadStorage(this)
        ]).pipe(
            share(),
            tap(([version, scenarioSettings]) => {
                this._version = version;
                this._scenarioSettings = scenarioSettings;
                this.readonly = this._checkReadonlyRights();
                this._onVersionLoaded();
                this._cdr.markForCheck();
            })
        );
    }

    protected _getRequiredDefinitions(): DefinitionKey[] {
        return [];
    }

    /**
     * When overridden should return default view-related settings for the page.
     */
    protected _getDefaultViewSettings(): any {
        return {};
    }

    /**
     * When overridden should return default data-related settings for the page.
     */
    protected _getDefaultPageSettings(): any {
        return {};
    }

    protected _checkReadonlyRights(): boolean {
        return this._version.is_readonly || this._version.locked;
    }

    protected _onVersionLoaded() {}

    protected _activate(): void {
        // Load data
        this._loadBootstrapData().subscribe(() => {
            this._onBootstrapDataLoaded();
        });

        /* if ( !this._pageSettings ) this._pageSettings = {} as any;
        _.merge( this._pageSettings, this.getDefaultPageSettings(), ( objValue, srcValue, key, obj, src ) => objValue );*/
    }

    // ----------------------------------------------------------------------------------
    //
    protected _configureLoadManager(): void {
        this._addLoaders(this._getLoaderArguments());
    }

    protected _getLoaderArguments(): LoaderArgumentsMap {
        return {};
    }

    protected _addLoaders(args: LoaderArgumentsMap): void {}

    protected _argsExcept(args: _.Dictionary<any>, ...names: string[]): _.Dictionary<any> {
        const res = _.clone(args);
        _.each(names, name => {
            delete res[name];
        });
        return res;
    }

    /**
     * Loads one or several data sets.
     *
     * @param toLoad Names of data sets to load.
     */
    protected _load(...toLoad: string[]): Subject<any> {
        return this._loadDataSets(toLoad, false);
    }

    private _loadDataSets(sets: string[], force: boolean): Subject<any> {
        // if (_.isEmpty(sets)) {
        //    throw Error("Missing data sets to load");
        // }

        const fn: (...names: string[]) => Observable<any[]> = !force
            ? this._loadManager.load.bind(this._loadManager)
            : this._loadManager.reload.bind(this._loadManager);

        const subj = new Subject();
        fn(...sets).subscribe({
            next: data => {
                this._lgConsole.debug("load: loading has completed.", sets);
                subj.next(data);
            },
            error: err => {
                if (err instanceof HttpErrorResponse) {
                    this._lgConsole.error("load: loading has failed.", sets, err);
                    this._onServerFailure(err);
                }
                subj.error(err);
            },
            complete: () => {
                subj.complete();
            }
        });
        return subj;
    }

    /**
     * If not data set names are specified, clears the arguments cache in LoadManager and reloads all visible page blocks.
     * Otherwise reloads only specified sets.
     *
     * @param toLoad Optional names of data sets to load.
     */
    protected _reload(...toLoad: string[]): Subject<any> {
        if (toLoad.length === 0) {
            // this.isReady = false;
            this._loadManager.flush();
            return this._loadVisibleDataSets();
            /* .then( () => {
                    this.isReady = true;
                } )*/
        } else {
            return this._loadDataSets(toLoad, true);
        }
    }

    protected _onServerFailure(
        error: HttpErrorResponse | (HttpErrorResponse & { force_quiet: boolean })
    ): void {
        if ("force_quiet" in error && error.force_quiet) return;

        let errorText = "Error status " + error.status;

        if (error.headers.get("force_quiet")) return;
        if (error.error != null) {
            if (error.error.Message != null) {
                errorText = error.error.Message;
                if (error.error.ExceptionMessage != null) {
                    errorText += `<br /><b>${error.error.ExceptionMessage}</b>`;
                }
                if (error.error.StackTrace != null) {
                    errorText += `<br /><div class="confirm-message__section confirm-message__scrollable" ><b>Stack:</b><br />
                        ${error.error.StackTrace.replace(/\n/g, "<br/>")}</div>`;
                }
            } else {
                const find = /<b>\s*Description:\s*<\/b>([^<]*)<br>/.exec(error.error.data);
                if (find) {
                    errorText = find[1];
                }
            }
        }

        this._confirm.alert(this._translate("FW.FAILED_REQUEST_TO_SERVER"), errorText, {
            columns: 5,
            button1: this._translate("FW.CLOSE")
        });
    }

    /**
     * Cancels loading of a specified data set. If it is not loading now, method just
     * won't do anything.
     *
     * @param name Name of the data set to cancel.
     */
    protected _cancelLoading(name: string): void {
        this._loadManager.cancel(name);
    }

    /**
     * Returns true if a specified data set is being loaded now.
     *
     * @param name Name of the data set to check.
     */
    isLoading(name: string): boolean {
        return this._loadManager.isLoading(name);
    }

    isLoadingAsObservable(name: string): Observable<boolean> {
        return this._loadManager
            .isLoadingAsObservable(name)
            .pipe(map(state => state || this._forceLoadingOverlay));
    }

    /**
     * Returns true if a specified data set is being loaded now.
     *
     * @param name Name of the data set to check.
     */
    isLoaded(name: string): boolean {
        return this._loadManager.isLoaded(name);
    }

    dataAsObservable(name: string): Observable<any> {
        return this._loadManager.dataAsObservable(name);
    }

    protected _loadObservedDataSets(): void {
        this._load(...this._loadManager.getObservedLoaders());
    }

    protected _loadVisibleDataSets(): Subject<any> {
        return this._load(
            ..._.uniq([...this._getVisibleDataSets(), ...this._loadManager.getObservedLoaders()])
        );
    }

    // ----------------------------------------------------------------------------------
    //
    /**
     * When implemented in a derived class, starts loading of initial bootstrap data
     * and returns a promise.
     */
    protected _loadBootstrapData(): Observable<any> {
        return this._loadVisibleDataSets();
    }

    /**
     * When bootstrap data is loaded, calls [[[initFilters]] and sets [[isReady]].
     */
    protected _onBootstrapDataLoaded(): void {
        // this.lgGaTracking.trackTime( "Page load time", "fullLoad", ( new Date() as any ) - this._pageLoadingStartTime );
        this._initialized = true;
        this._cdr.markForCheck();
    }

    // ----------------------------------------------------------------------------------
    // Page blocks
    protected _configurePageBlocks(): void {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const makePageBlockIsLoadingFunction = (block: PageBlock) => {
            const tests = _.map(block.dataSets, y => `this.isLoading( "${y}" )`);
            const body = `return ${tests.join(" || ")};`;
            const func = new Function(body); // eslint-disable-line no-new-func
            return func.bind(this);
        };

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const makePageBlockIsLoadedFunction = (block: PageBlock) => {
            const tests = _.map(block.dataSets, y => `this.isLoaded( "${y}" )`);
            const body = `return ${tests.join(" && ")};`;
            const func = new Function(body); // eslint-disable-line no-new-func
            return func.bind(this);
        };

        const pageBlocks = this._getPageBlocks();
        this._pageBlocks = _.mapValues(pageBlocks, x => ({
            visible: x.visible,
            dataSets: x.dataSets,
            isLoading: makePageBlockIsLoadingFunction(x),
            isLoaded: makePageBlockIsLoadedFunction(x)
        }));
    }

    /**
     * When overridden should return the page UI blocks. UI block has a name
     * and it could be visible or invisible now depending on some criteria.
     * If block is visible we can load the data it depends on by requesting
     * all data sets defined in "dataSets". If block is not visible, then
     * it is not necessary to load data.
     *
     * Usage example:
     * ```typescript
     * protected getPageBlocks() {
     *     return {
     *         specialisms: {
     *             visible: () => this.isProductTab() && !this.selectedProduct,
     *             dataSets: ["specialisms"]
     *         },
     *         ...
     *      };
     * }
     * ```
     */
    protected _getPageBlocks(): _.Dictionary<PageBlock> {
        return {};
    }

    /**
     * Returns if the given page UI block is visible at the moment.
     *
     * @param name Name of the UI block.
     */
    _isPageBlockVisible(name: string): boolean {
        if (!this._pageBlocks) {
            return false;
        }

        const block = this._pageBlocks[name];

        if (!block) {
            throw Error(`Page block "${name}" is unknown.`);
        }

        return block.visible();
    }

    /**
     * Loads all visible page blocks.
     */
    protected _loadPageBlocks(): Subject<any> {
        return this._load(...this._getVisibleDataSets());
    }

    protected _getVisibleDataSets(): string[] {
        return _.chain(this._pageBlocks)
            .valuesIn()
            .filter(x => x.visible())
            .map((x: PageBlockExt) => x.dataSets)
            .flatten()
            .uniq()
            .value();
    }

    /**
     * Check if any data set of a given page block is still loading.
     *
     * @param name
     */
    _isPageBlockLoading(name: string): boolean {
        if (!this._initialized && !this._preparationFailed) return true;

        const block = this._pageBlocks[name];

        if (!block) {
            this._lgConsole.warn(`isPageBlockLoading: Unknown block "${name}" requested.`);
            return false;
        }

        return block.isLoading();
    }

    /**
     * Check if any data set of a given page block is still loading.
     *
     * @param name
     */
    _isPageBlockLoaded(name: string): boolean {
        if (!this._initialized) return false;

        const block = this._pageBlocks[name];

        if (!block) {
            this._lgConsole.warn(`isPageBlockLoading: Unknown block "${name}" requested.`);
            return false;
        }

        return block.isLoaded();
    }

    // ----------------------------------------------------------------------------------
    //
    _translate(translationId: string, interpolateParams?: any, interpolationId?: string): string;
    _translate(
        translationId: string[],
        interpolateParams?: any,
        interpolationId?: string
    ): { [key: string]: string };

    _translate(
        translationId: string | string[],
        interpolateParams?: any,
        interpolationId?: string
    ): any {
        return this._lgTranslate.translate(translationId as any, interpolateParams);
    }

    // ----------------------------------------------------------------------------------
    //
    public ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }
}
